import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { cachedGet } from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import AIChat from '../components/AIChat';
import { Sparkles } from 'lucide-react';

export default function Home() {
  const { t, lang } = useLang();
  const [categories, setCategories] = useState([]);
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    cachedGet('/categories').then(({ data }) => setCategories(data));
    cachedGet('/recipes').then(({ data }) => setRecipes(data));
  }, []);

  return (
    <div className="space-y-10" data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl grain isolate">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1774106455374-8415d96cf11e?w=1600&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#2C2420]/70 via-transparent to-[#C05A42]/45" />
        </div>
        <div className="relative z-10 px-6 py-16 sm:px-12 sm:py-24 text-white max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold border border-white/30 text-white">
            <Sparkles className="h-3.5 w-3.5" /> {t('aiAssistant')}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] text-white" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.5)' }}>
            {t('appName')}
          </h1>
          <p className="mt-4 text-lg text-white max-w-xl leading-relaxed" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>{t('tagline')}</p>
        </div>
      </section>

      {/* Categories - bento */}
      <section>
        <div className="flex items-end justify-between mb-5">
          <h2 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight">{t('categories')}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="categories-grid">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              data-testid={`category-${c.id}`}
              className="group relative overflow-hidden rounded-3xl aspect-[4/5] fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <img src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <h3 className="absolute bottom-4 start-4 end-4 text-white text-xl font-display font-semibold drop-shadow-md">
                {lang === 'ar' ? c.name_ar : c.name_en}
              </h3>
            </Link>
          ))}
        </div>
      </section>

      {/* Suggested recipes */}
      <section>
        <div className="flex items-end justify-between mb-5">
          <h2 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight">{t('suggested')}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.slice(0, 6).map((r, i) => <RecipeCard key={r.recipe_id} recipe={r} index={i} />)}
        </div>
      </section>

      <AIChat />
    </div>
  );
}
